import { motion } from "framer-motion";
import { portfolioData } from "../../utils/constants";

const Portfolio = () => {
  return (
    <section
      className="relative bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 overflow-hidden px-p83 py-10 500:py-14 min-h-[90vh] flex flex-col justify-center"
      style={{
        backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(0, 120, 209, 0.12), transparent 50%)'
      }}
      id="portfolio"
    >
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-primary-200/20 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" style={{ right: '-144px' }} />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-orange/10 rounded-full blur-3xl translate-y-1/2 pointer-events-none" style={{ left: '-192px' }} />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header Section */}
        <motion.div
          className="text-center mb-8 500:mb-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500/20 to-primary-600/10 border border-primary-500/30 rounded-full mb-4 backdrop-blur-sm"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <span className="w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
            <span className="text-primary-300 font-poppins-semibold text-sm">
              Our Work
            </span>
          </motion.div>

          <h2 className="font-merry-bold tracking-tight text-2xl 500:text-3xl 700:text-4xl 900:text-5xl mb-3">
            Featured{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-primary-500 font-bold">
                Projects
              </span>
              <motion.span
                className="absolute bottom-2 left-0 right-0 h-3 bg-primary-500/20 -z-0 rounded-full"
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </h2>

          <motion.p
            className="text-neutral-300 text-sm 500:text-base max-w-2xl mx-auto font-poppins-regular"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Explore our portfolio of innovative solutions that have transformed businesses and delivered exceptional results.
          </motion.p>
        </motion.div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 500:gap-8 w-full">
          {portfolioData.map((project, index) => (
            <motion.div
              key={project.id}
              className="group bg-gradient-to-br from-neutral-800/95 via-neutral-800 to-neutral-900/95 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:shadow-primary-500/20 transition-all duration-500 border border-neutral-700/50 backdrop-blur-sm hover:border-primary-500/50"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              {/* Gradient overlay on hover */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 pointer-events-none"
                initial={false}
              />

              {/* Project Image */}
              <div className="relative h-48 500:h-56 overflow-hidden bg-gradient-to-br from-neutral-700 to-neutral-800">
                <img
                  src={project.image}
                  alt={`${project.title} - ${project.description.substring(0, 50)}...`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "https://via.placeholder.com/800x600/0078D4/FFFFFF?text=" + encodeURIComponent(project.title);
                  }}
                />
                {/* Gradient overlay on image */}
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 -left-full group-hover:left-full w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-all duration-1000"
                  initial={false}
                />
              </div>

              {/* Project Content */}
              <div className="p-6 500:p-8 relative z-10">
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-3 py-1.5 bg-gradient-to-r from-primary-500/20 to-primary-600/10 border border-primary-500/30 text-primary-300 text-xs font-poppins-medium rounded-full backdrop-blur-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h3 className="text-xl 500:text-2xl font-merry-bold text-neutral-100 mb-3 group-hover:text-primary-400 transition-colors duration-300">
                  {project.title}
                </h3>

                <p className="text-neutral-300 text-sm 500:text-base font-poppins-regular leading-relaxed mb-5 line-clamp-3">
                  {project.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-neutral-700/50">
                  <span className="text-xs 500:text-sm font-poppins-semibold text-primary-400 bg-gradient-to-r from-primary-500/10 to-primary-600/5 px-3 py-1.5 rounded-lg border border-primary-500/20">
                    {project.category}
                  </span>
                  <span className="text-xs 500:text-sm font-poppins-medium text-neutral-400">
                    {project.year}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;

